/**
 * Quick prescribe panel — standalone single-treatment form used outside of a
 * consultation context. Rendered inside a dialog on the patient page.
 *
 * Submits the treatment to the server immediately on save and closes.
 */
import { useState } from 'react'

import type { PrescribingContext } from '@snootsvet/shared'

import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetPatient } from '@/frontend/hooks/api/patients/use-get-patient'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'

import { useCreateTreatments } from './hooks/use-create-treatments'
import { PreviousTreatmentsList } from './previous-treatments-list'
import { TreatmentForm } from './treatment-form'
import type { FormTreatmentItem, TreatmentCategory } from './treatments.types'
import { toApiItem } from './treatments.utils'

interface QuickPrescribePanelProps {
  patientId: string
  /** Pre-populated values — used by the re-prescribe flow. */
  defaultValues?: FormTreatmentItem
  /** Context for the treatment creation — defaults to 'standalone'. */
  prescribingContext?: PrescribingContext
  onSuccess?: () => void
  onCancel?: () => void
}

export function QuickPrescribePanel({
  patientId,
  defaultValues,
  prescribingContext = 'standalone',
  onSuccess,
  onCancel,
}: QuickPrescribePanelProps) {
  const { region } = useViewerRegion()
  const { data: staff } = useGetAuthenticatedStaff()
  const isRemoteVet = staff?.role === 'remote_vet'
  // Re-prescribing a vaccine opens the vaccine form here, which needs the species.
  const { data: patient } = useGetPatient(patientId, (r) => r.patient)
  const { mutateAsync: createTreatment } = useCreateTreatments()

  const [defaults, setDefaults] = useState<FormTreatmentItem | undefined>(defaultValues)
  /** Incremented to force-remount the form when loading a previous treatment — the form only reads defaultValues on mount. */
  const [formKey, setFormKey] = useState(0)
  const initialCategory = defaults?.treatmentCategory ?? 'prescription'
  const [category, setCategory] = useState<TreatmentCategory>(initialCategory)
  const [isSaving, setIsSaving] = useState(false)

  function handleLoadPreviousTreatment(item: FormTreatmentItem) {
    setDefaults(item)
    setCategory(item.treatmentCategory)
    setFormKey((k) => k + 1)
  }

  async function handleSave(item: FormTreatmentItem) {
    setIsSaving(true)
    try {
      await createTreatment({
        patientId,
        items: [toApiItem(item, region)],
      })
      onSuccess?.()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      <TreatmentForm
        patientId={patientId}
        category={category}
        onCategoryChange={setCategory}
        defaults={defaults}
        prescribingContext={prescribingContext}
        patientSpecies={patient?.species ?? ''}
        defaultCreateTask={isRemoteVet}
        onSave={handleSave}
        onCancel={() => onCancel?.()}
        saving={isSaving}
        remountKey={formKey}
      />
      <PreviousTreatmentsList
        patientId={patientId}
        prescribingContext={prescribingContext}
        disabled={isSaving}
        onSelect={handleLoadPreviousTreatment}
      />
    </div>
  )
}
